import type { JwtPayload } from '@/lib/api-client';
import type { LoginResponse, Tenant, User, UserRole } from '../types/auth.types';
import { normalizePermissionClaims } from './permission-matcher';

const ROLE_PRIORITY: UserRole[] = [
  'SUPER_ADMIN',
  'TENANT_ADMIN',
  'CRM_ADMIN',
  'SALES_DIRECTOR',
  'HR_ADMIN',
  'HR_MANAGER',
  'PAYROLL_MANAGER',
  'SALES_MANAGER',
  'DEPARTMENT_HEAD',
  'PEOPLE_MANAGER',
  'TEAM_LEAD',
  'SALES_REP',
  'MANAGER',
  'ATTENDANCE_GOVERNANCE_APPROVER',
  'EMPLOYEE',
];

const ROLE_MAP: Record<string, UserRole> = {
  super_admin: 'SUPER_ADMIN',
  superadmin: 'SUPER_ADMIN',
  tenant_admin: 'TENANT_ADMIN',
  tenantadmin: 'TENANT_ADMIN',
  crm_admin: 'CRM_ADMIN',
  crmadmin: 'CRM_ADMIN',
  hr_admin: 'HR_ADMIN',
  hr_manager: 'HR_MANAGER',
  hrmanager: 'HR_MANAGER',
  payroll_manager: 'PAYROLL_MANAGER',
  payrollmanager: 'PAYROLL_MANAGER',
  department_head: 'DEPARTMENT_HEAD',
  people_manager: 'PEOPLE_MANAGER',
  team_lead: 'TEAM_LEAD',
  sales_director: 'SALES_DIRECTOR',
  sales_manager: 'SALES_MANAGER',
  sales_rep: 'SALES_REP',
  manager: 'MANAGER',
  attendance_governance_approver: 'ATTENDANCE_GOVERNANCE_APPROVER',
  employee: 'EMPLOYEE',
};

export function mapAuthRole(role: string | undefined): UserRole {
  if (!role) {
    return 'EMPLOYEE';
  }

  return ROLE_MAP[role.toLowerCase()] ?? 'EMPLOYEE';
}

export function getHighestAuthRole(roles: readonly string[] | undefined): UserRole {
  const mapped = (roles ?? []).map(mapAuthRole);
  return ROLE_PRIORITY.find((role) => mapped.includes(role)) ?? 'EMPLOYEE';
}

export function normalizeServerPermissions(permissions: readonly string[] | undefined): string[] {
  return normalizePermissionClaims(permissions ?? []);
}

export function mapLoginResponseToAuthSession(
  loginData: LoginResponse,
  jwtPayload: JwtPayload | null,
): { user: User; tenant?: Tenant } {
  if (!loginData?.accessToken || !loginData.user) {
    throw new Error('Login response missing required session data');
  }

  const tenant = loginData.tenant
    ? {
        id: loginData.tenant.id,
        name: loginData.tenant.name || 'Unknown Organization',
        code: loginData.tenant.code || loginData.tenant.slug || loginData.tenant.id,
        slug: loginData.tenant.slug,
        plan: loginData.tenant.plan,
        timezone: loginData.tenant.timezone,
        locale: loginData.tenant.locale,
        currency: loginData.tenant.currency,
        dateFormat: loginData.tenant.dateFormat,
        timeFormat: loginData.tenant.timeFormat,
      }
    : undefined;

  const tenantId = jwtPayload?.tenantId || loginData.user.tenantId || tenant?.id;
  if (!tenantId) {
    throw new Error('Login response missing tenant context');
  }

  const fullName =
    loginData.user.fullName ||
    [loginData.user.firstName, loginData.user.middleName, loginData.user.lastName]
      .filter(Boolean)
      .join(' ')
      .trim() ||
    loginData.user.email;

  return {
    user: {
      ...loginData.user,
      role: getHighestAuthRole(loginData.user.roles),
      permissions: normalizeServerPermissions(loginData.user.permissions),
      tenantId,
      middleName: loginData.user.middleName,
      employeeId: loginData.user.employeeId || jwtPayload?.employeeId,
      departmentId: loginData.user.departmentId || jwtPayload?.departmentId,
      departmentName: loginData.user.departmentName,
      departmentCode: loginData.user.departmentCode,
      positionId: loginData.user.positionId || jwtPayload?.positionId,
      positionTitle: loginData.user.positionTitle,
      positionCode: loginData.user.positionCode,
      isChiefPosition: loginData.user.isChiefPosition ?? jwtPayload?.isChiefPosition,
      teamIds: loginData.user.teamIds || jwtPayload?.teamIds || [],
      managedTeamIds: loginData.user.managedTeamIds || jwtPayload?.managedTeamIds || [],
      managedDepartmentId: loginData.user.managedDepartmentId || jwtPayload?.managedDepartmentId,
      fullName,
      isActive: loginData.user.isActive ?? true,
      createdAt: loginData.user.createdAt || new Date().toISOString(),
      updatedAt: loginData.user.updatedAt || new Date().toISOString(),
    },
    tenant,
  };
}
